/**
 * DTOs du domaine wallet — fidèles aux schémas du guide §6.3.
 * Énumérations : annexe A via `@/features/shared`.
 */
import type {
  BeneficiaryType,
  CustomerSegment,
  DestinationExternalType,
  FeePayerWallet,
  TransactionStatus,
  TransactionType,
  WalletKycLevel,
  WalletOwnerType,
  WalletStatus,
} from "@/features/shared/api/enums";
import type { TransactionDisputeStatus } from "@/features/wallet/api/enums";

/** Portefeuille (WalletDto du guide — corps de `add` et réponses). */
export interface WalletDto {
  id?: string;
  walletNumber?: string;
  ownerType: WalletOwnerType;
  /** Réf. logique kyc-service.party.id. */
  ownerPartyId?: string;
  /** Nom d'affichage du propriétaire renvoyé par le wallet-service (null pour NON_MEMBER). */
  ownerName?: string | null;
  keycloakUserId?: string;
  /** Défaut serveur : GNF. */
  currency?: string;
  /** Défaut serveur : TIER0. */
  kycLevel?: WalletKycLevel;
  /** Défaut serveur : PENDING_KYC. */
  status?: WalletStatus;
  /** Verrou optimiste (réponses). */
  version?: number;
  createdAt?: string;
  updatedAt?: string;
}

/* ----------------------------------- Transactions ------------------------------- */

/** Transaction (TransactionDto du guide §6.3 — réponses de initiate/all/{id}). */
export interface TransactionDto {
  id: string;
  /** Référence métier (ex. FPT-20260909-000147). */
  reference: string;
  productCode: string;
  type: TransactionType;
  /** Nullable (ex. `AGENT_CASH_IN` depuis les espèces d'un agent — guide v2.0). */
  sourceWalletId?: string | null;
  destinationWalletId?: string | null;
  destinationExternalType: DestinationExternalType;
  /** Référence externe (compte bancaire / mobile money) — absente de la réponse d'exemple Postman, renvoyée selon le type de destination. */
  destinationExternalRef?: string | null;
  /** Montant principal, entier en unité mineure (§4.3). */
  amountMinor: number;
  /** Frais calculés par wallet-service à l'initiation (unité mineure). */
  feeAmountMinor: number;
  feePayer: FeePayerWallet;
  agentId?: string | null;
  agentCommissionAmountMinor?: number | null;
  currency: string;
  status: TransactionStatus;
  failureReason?: string | null;
  externalProviderReference?: string | null;
  initiatedAt?: string;
  completedAt?: string | null;
  billerCode?: string | null;
  customerReference?: string | null;
}

/**
 * Corps de `POST /Transactions/initiate` (collection Postman, guide §5.3) —
 * idempotence OBLIGATOIRE (en-tête Idempotency-Key). Les champs nullables
 * absents restent `undefined` (jamais envoyés) plutôt que `null`.
 */
export interface InitiateTransactionRequestDto {
  type: TransactionType;
  productCode: string;
  providerId?: string | null;
  sourceWalletId: string;
  destinationWalletId?: string | null;
  destinationExternalType: DestinationExternalType;
  destinationExternalRef?: string | null;
  /** Montant principal, entier en unité mineure (§4.3). */
  amountMinor: number;
  /** Défaut serveur : GNF. */
  currency: string;
  customerSegment: CustomerSegment;
  agentId?: string | null;
  agentPartyId?: string | null;
  promoCode?: string | null;
  /** Party payeur (réf. logique kyc-service.party.id). */
  partyId?: string | null;
  billerCode?: string | null;
  customerReference?: string | null;
}

/** Filtre de `POST /Transactions/all` (TransactionFilter — tous champs optionnels). */
export interface TransactionFilter {
  sourceWalletId?: string;
  destinationWalletId?: string;
  productCode?: string;
  status?: TransactionStatus;
  type?: TransactionType;
}

/**
 * Statuts transitoires (§5.3) : la transaction attend l'écriture comptable et
 * sa confirmation — la page détail poll tant que le statut en fait partie.
 */
export const TRANSIENT_TRANSACTION_STATUSES: readonly TransactionStatus[] = [
  "PENDING",
  "AWAITING_LEDGER",
];

/* -------------------------------------- Wallets --------------------------------- */

/** Filtre de `POST /Wallets/all` (WalletFilter — tous champs optionnels, §4.2). */
export interface WalletFilter {
  ownerPartyId?: string;
  ownerType?: WalletOwnerType;
  status?: WalletStatus;
}

/**
 * Soldes d'un portefeuille (`GET /Wallets/{id}/balance`) — tous les montants
 * sont des entiers en unité mineure (§4.3). `availableBalanceMinor` est le
 * montant réellement dépensable (hors blocages), `pendingBalanceMinor` les
 * blocages en cours, `ledgerBalanceMinor` le solde comptable.
 */
export interface WalletBalanceDto {
  walletId?: string;
  currency?: string;
  availableBalanceMinor?: number;
  pendingBalanceMinor?: number;
  ledgerBalanceMinor?: number;
}

/* ----------------------------------- Beneficiaries ------------------------------ */

/**
 * Bénéficiaire enregistré (BeneficiaryDto — corps de `add` et réponses).
 * Le numéro de compte est renvoyé en clair ; sa colonne de hash ne l'est
 * jamais (chiffré pour le stockage côté serveur).
 */
export interface BeneficiaryDto {
  id?: string;
  walletId?: string;
  label?: string;
  type?: BeneficiaryType;
  institutionCode?: string;
  accountNumber?: string;
  isFavorite?: boolean;
}

/** Corps de `POST /Beneficiaries/add`. */
export interface BeneficiaryPayload {
  walletId: string;
  label: string;
  type: BeneficiaryType;
  institutionCode?: string;
  accountNumber: string;
  isFavorite?: boolean;
}

/* --------------------------------- Devis de frais -------------------------------- */

/**
 * Corps d'un devis — `POST /fee-quotes` (§6.5). Aligné sur le body Postman
 * `FeeQuoteRequestDto` : `transactionReference` est **requis** par le
 * fee-engine (désérialisation JSON). Les identifiants optionnels partent
 * en `null` explicite (`providerId`, `beneficiaryPartyId`, `promoCode`,
 * `partyId`).
 */
export interface TransactionFeeQuoteRequestDto {
  productCode: string;
  providerId?: string | null;
  customerSegment: CustomerSegment;
  feePurpose: "CUSTOMER_FEE";
  amountMinor: number;
  currency: string;
  /**
   * Référence de corrélation du devis (journalisation fee-engine).
   * L'initiation n'existe pas encore à la preview : UUID client.
   * Distinct de `customerReference` (initiate wallet).
   */
  transactionReference: string;
  beneficiaryPartyId?: string | null;
  promoCode?: string | null;
  partyId?: string | null;
}

/** Réponse d'un devis — sous-ensemble du DTO canonique (§6.5). */
export interface TransactionFeeQuoteResponseDto {
  grossFeeAmountMinor: number;
  discountAmountMinor: number;
  netFeeAmountMinor: number;
  currency: string;
  noActiveRule: boolean;
}

/* -------------------------- EmoneyIssuances (guide v2.0) -------------------------- */

/** Corps de `POST /EmoneyIssuances/issue` et `/redeem` — idempotence obligatoire. */
export interface EmoneyIssuanceRequestDto {
  approvalRequestId: string;
  walletId: string;
  amountMinor: number;
  currency: string;
  bankReference: string;
}

/** Rapprochement quotidien (`GET /EmoneyIssuances/reconciliation?currency=`). */
export interface EmoneyReconciliationDto {
  outstandingEmoneyMinor: number;
  netIssuedMinor: number;
  differenceMinor: number;
  currency: string;
  generatedAt?: string;
}

/* --------------------------- WalletQrCodes (guide v2.0) --------------------------- */

/** QR de réception (`GET /WalletQrCodes/me`). */
export interface WalletQrCodeMeDto {
  walletId: string;
  walletNumber?: string;
  token: string;
  expiresAt: string;
  rotationSeconds?: number;
}

/** Lecture d'un QR tiers (`GET /WalletQrCodes/resolve/{token}`). */
export interface WalletQrCodeResolveDto {
  walletId: string;
  walletNumber?: string;
  currency?: string;
  canReceive: boolean;
}

/** Corps de `POST /Transactions/scan-transfer` — idempotence obligatoire. */
export interface ScanTransferRequestDto {
  qrToken: string;
  amountMinor: number;
  sourceWalletId?: string;
  customerReference?: string;
}

/* ------------------------------ Statements (v2.3) ------------------------------- */

/**
 * Corps de `POST /Statements/request` (guide v2.3 §6.3). `walletId` est
 * facultatif si le compte n'en détient qu'un, obligatoire au-delà.
 */
export interface StatementRequestDto {
  walletId?: string | null;
  /** ISO date (YYYY-MM-DD). */
  from: string;
  /** ISO date (YYYY-MM-DD). */
  to: string;
}

/**
 * Réponse de `POST /Statements/request` (exemple de la collection v3.1).
 * Confirmation d'envoi — l'adresse du titulaire n'est rendue que masquée
 * (`maskedRecipient`, ex. m***@domaine). `downloadUrl` porte le chemin
 * passerelle du PDF (`GET /Statements/requests/{statementId}/download`),
 * disponible immédiatement (relevé recomposé à la demande).
 */
export interface StatementRequestResponse {
  walletId?: string;
  walletNumber?: string;
  /** ISO date (YYYY-MM-DD). */
  from?: string;
  /** ISO date (YYYY-MM-DD). */
  to?: string;
  transactionCount?: number;
  fileName?: string;
  maskedRecipient?: string;
  notificationLogId?: string;
  sentAt?: string;
  /** Chemin passerelle du PDF — contient le `statementId`. */
  downloadUrl?: string;
  /**
   * Absent de l'exemple v3.1 (l'identifiant se déduit de `downloadUrl`) —
   * champ défensif si le serveur l'expose un jour à plat.
   */
  statementId?: string;
}

/* --------------------------- Non-Member codes (v2.4) --------------------------- */

/** Corps de `POST /NonMemberCodes/verify` (guide v2.4 §6.3). Sans effet financier. */
export interface NonMemberCodeVerifyRequest {
  code: string;
  phone: string;
}

/** Réponse de `POST /NonMemberCodes/verify`. */
export interface NonMemberCodeVerifyResponse {
  code: string;
  amountMinor: number;
  currency: string;
  recipientDisplayName?: string;
  expiresAt: string;
}

/** Corps de `POST /NonMemberCodes/redeem` (guide v2.4 §6.3). Idempotence obligatoire. */
export interface NonMemberCodeRedeemRequest {
  code: string;
  phone: string;
  agentId: string;
  agentPartyId: string;
}

/** Réponse de `POST /NonMemberCodes/redeem` — transaction AGENT_CASH_OUT. */
export type NonMemberCodeRedeemResponse = TransactionDto;

/* ------------------- Wallets lookups & workers (v2.4 / v2.5) ------------------- */

/** Corps de `POST /Wallets/resolve-by-phone` (guide v2.4 §6.3). */
export interface WalletResolveByPhoneRequest {
  phone: string;
}

/** Réponse de `POST /Wallets/resolve-by-phone`. */
export interface WalletResolveByPhoneResponse {
  walletId: string;
  walletNumber: string;
  ownerPartyId: string;
  displayName?: string;
  currency?: string;
  canReceive?: boolean;
}

/** Corps de `POST /Wallets/apply-kyc-approval` (guide v2.5 §6.3). */
export interface ApplyKycApprovalRequest {
  partyId: string;
  level: WalletKycLevel;
  keycloakUserId: string;
}

/* ------------------ Transactions by-reference workers (v2.5) ------------------ */

export interface ByReferenceRequest {
  reference: string;
  reason?: string | null;
}

/** Pour `reverse-by-reference`, `reason` est obligatoire. */
export interface ReverseByReferenceRequest {
  reference: string;
  reason: string;
}

/* ---------------------------- QR agent statique (v2.4) --------------------------- */

/** Réponse de `GET /WalletQrCodes/agent/me` (guide v2.4 §6.3). */
export interface WalletQrCodeAgentMeDto {
  agentId: string;
  agentPartyId?: string;
  payloadToken: string;
  qrImageBase64?: string;
}

/* --------------------- Contestations de transfert (v3.1) ------------------------ */

/**
 * Contestation de transfert (TransactionDisputeDto — réponses de `open`,
 * `all`, `{id}`, `{id}/reject`, guide v3.1). Montants en unité mineure
 * (§4.3) : `contestedAmountMinor` = montant contesté, `heldAmountMinor` =
 * gel effectif chez le bénéficiaire, `unheldAmountMinor` = part non gelée
 * (déjà dépensée). NB : le plan d'intégration nomme le montant contesté
 * `disputedAmountMinor` — la collection et le guide v3.1 exposent
 * `contestedAmountMinor`, qui fait foi.
 */
export interface TransactionDisputeDto {
  id: string;
  transactionId: string;
  /** Référence métier de la transaction contestée (ex. FPT-20260909-000001). */
  transactionReference?: string;
  beneficiaryWalletId?: string;
  openedByKeycloakUserId?: string;
  reason?: string;
  status: TransactionDisputeStatus;
  contestedAmountMinor: number;
  /** Gel effectif sur le portefeuille du bénéficiaire. */
  heldAmountMinor: number;
  /** Part du montant contesté qui n'a pas pu être gelée (déjà dépensée). */
  unheldAmountMinor?: number;
  currency?: string;
  openedAt?: string;
  /** Échéance du gel (72 h — `Disputes__HoldDurationHours`). */
  expiresAt?: string;
  closedAt?: string | null;
  closedByKeycloakUserId?: string | null;
  resolutionNote?: string | null;
  /** Issue — mentionnée par le plan d'intégration, absente des exemples v3.1. */
  outcome?: string | null;
}

/** Filtre de `POST /TransactionDisputes/all` (TransactionDisputeFilter). */
export interface TransactionDisputeFilter {
  status?: TransactionDisputeStatus;
  transactionId?: string;
}

/**
 * Corps de `POST /TransactionDisputes/open` — idempotence obligatoire.
 * `reason` : 5 à 500 caractères (jamais transmise au bénéficiaire).
 */
export interface OpenTransactionDisputeRequestDto {
  transactionId: string;
  reason: string;
}

/**
 * Corps de `POST /TransactionDisputes/{id}/reject` — idempotence
 * obligatoire. `note` : 5 à 500 caractères (motif du rejet).
 */
export interface RejectTransactionDisputeRequestDto {
  note: string;
}

/* ------------- SavingsVaultWallets (v3.1 — INTERNAL, parité) ------------- */

/**
 * INTERNAL — flashpay-service only. DTO des routes `SavingsVaultWallets`
 * (wallet-service), appelées exclusivement par savings-service : une
 * application n'appelle jamais `/gw/wallet/api/v1/SavingsVaultWallets/*`,
 * elle passe par `/gw/savings`. Typés d'après les exemples de la collection
 * v3.1 pour parité documentaire — aucun hook, aucun écran.
 */

/** Corps de `POST /SavingsVaultWallets` (idempotence obligatoire). */
export interface SavingsVaultWalletCreateRequestDto {
  ownerKeycloakUserId: string;
  mainWalletId: string;
}

/** Réponse de `POST /SavingsVaultWallets` (201) — WalletDto (purpose SAVINGS_VAULT). */
export type SavingsVaultWalletCreateResponseDto = WalletDto;

/** Corps de `POST /SavingsVaultWallets/balances` (≤ 200 identifiants par appel). */
export interface SavingsVaultWalletBalancesRequestDto {
  walletIds: string[];
}

/** Solde d'un portefeuille-coffre (réponse de `POST /SavingsVaultWallets/balances`). */
export interface SavingsVaultWalletBalanceDto {
  walletId: string;
  currency: string;
  ledgerBalanceMinor: number;
  pendingBalanceMinor: number;
  availableBalanceMinor: number;
  /** Échéance de blocage recopiée du coffre (null = débloqué). */
  lockedUntil?: string | null;
  status?: WalletStatus;
}

/**
 * Direction d'un mouvement coffre (`POST /SavingsVaultWallets/transfers`) :
 * DEPOSIT = principal → coffre, WITHDRAWAL = coffre → principal.
 * NB : l'exemple de corps de la collection v3.1 montre `"DEBIT"` — écart
 * connu, la description de l'entrée (DEPOSIT|WITHDRAWAL) fait foi.
 */
export type SavingsVaultWalletTransferDirection = "DEPOSIT" | "WITHDRAWAL";

/**
 * Corps de `POST /SavingsVaultWallets/transfers` (idempotence obligatoire,
 * idempotent sur `movementId` — clé `SAVINGS:{movementId}`).
 */
export interface SavingsVaultWalletTransferRequestDto {
  movementId: string;
  direction: SavingsVaultWalletTransferDirection;
  vaultWalletId: string;
  mainWalletId: string;
  ownerKeycloakUserId: string;
  amountMinor: number;
  currency: string;
}

/** Réponse de `POST /SavingsVaultWallets/transfers` — transaction TRANSFER_INTERNAL. */
export type SavingsVaultWalletTransferResponseDto = TransactionDto;

/** Corps de `PUT /SavingsVaultWallets/{id}/lock` (idempotence obligatoire). */
export interface SavingsVaultWalletLockRequestDto {
  /** Échéance UTC du blocage ; null = débloqué. */
  lockedUntil: string | null;
}

/** Réponse de `GET /SavingsVaultWallets/deletable?after=&limit=` (curseur). */
export interface SavingsVaultWalletDeletableResponseDto {
  walletIds: string[];
  /** Curseur à repasser dans `after` ; null en fin de parcours. */
  nextAfter: string | null;
}

/* ------------ PayrollDisbursements (v4.1 — INTERNAL, parité) ------------ */

/**
 * INTERNAL — flashpay-service only. DTO des routes `PayrollDisbursements`
 * (wallet-service), appelées exclusivement par payroll-service pour exécuter
 * ou simuler une paie de masse : une application n'appelle jamais
 * `/gw/wallet/api/v1/PayrollDisbursements/*`, elle passe par
 * `/gw/payroll/api/v1/PayrollBatches/*`. Typés d'après les exemples de la
 * collection v4.1 pour parité documentaire — aucun hook, aucun écran.
 */

/** Statut d'une paie côté wallet-service — annexe A v4.1. */
export type PayrollDisbursementStatus =
  | "AWAITING_LEDGER"
  | "COMPLETED"
  | "FAILED";

/** Qui supporte les frais — SENDER (marchand) ou RECEIVER (déduit du salaire). */
export type PayrollDisbursementFeePayer = "SENDER" | "RECEIVER";

/** Ligne d'une paie de masse envoyée à wallet-service. */
export interface PayrollDisbursementLineDto {
  lineId: string;
  employeeName: string;
  employeeReference?: string | null;
  phone: string;
  amountMinor: number;
}

/**
 * Corps de `POST /PayrollDisbursements` (idempotence obligatoire,
 * clé portée par `disbursementId`).
 */
export interface PayrollDisbursementRequestDto {
  disbursementId: string;
  sourceWalletId: string;
  ownerKeycloakUserId: string;
  employerName: string;
  feePayer: PayrollDisbursementFeePayer;
  currency: string;
  lines: PayrollDisbursementLineDto[];
}

/**
 * Corps de `POST /PayrollDisbursements/preview` — simulation sans mouvement
 * d'argent. Même structure que `PayrollDisbursementRequestDto`.
 */
export type PayrollDisbursementPreviewRequestDto = PayrollDisbursementRequestDto;

/** État d'une ligne au terme de la simulation. */
export interface PayrollDisbursementPreviewLineDto {
  lineId: string;
  /** `MEMBER` = portefeuille FlashPay actif, `NON_MEMBER` = code de retrait. */
  beneficiaryType: "MEMBER" | "NON_MEMBER";
  /** Validée ou refusée (plafond, numéro inconnu…). */
  isPayable: boolean;
  /** Motif si non payable (plafond non-membre, portefeuille suspendu…). */
  invalidReason?: string | null;
  grossAmountMinor: number;
  feeAmountMinor: number;
  netAmountMinor: number;
}

/** Réponse de `POST /PayrollDisbursements/preview`. */
export interface PayrollDisbursementPreviewResponseDto {
  disbursementId: string;
  currency: string;
  feePayer: PayrollDisbursementFeePayer;
  availableBalanceMinor: number;
  grossAmountMinor: number;
  feeAmountMinor: number;
  debitAmountMinor: number;
  sufficientBalance: boolean;
  lines: PayrollDisbursementPreviewLineDto[];
}

/** Réponse de `POST /PayrollDisbursements` (201) — statut initial de la paie. */
export interface PayrollDisbursementCreateResponseDto {
  disbursementId: string;
  disbursementReference: string;
  status: PayrollDisbursementStatus;
  paidCount: number;
  sourceBalanceAfterMinor: number;
}

/** Réponse de `GET /PayrollDisbursements/{id}`. */
export interface PayrollDisbursementStatusDto {
  disbursementId: string;
  disbursementReference: string;
  status: PayrollDisbursementStatus;
  paidCount: number;
  sourceBalanceAfterMinor?: number;
  lines: Array<{
    lineId: string;
    status: "PENDING" | "PROCESSING" | "PAID" | "FAILED" | "REJECTED";
    transactionReference?: string | null;
    failureReason?: string | null;
  }>;
}
